import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { FC, useCallback, useEffect, useState } from "react";
import {
  Collaborator,
  CollaboratorOpenSessionsSummary,
  CollaboratorPaymentResult,
  getCollaboratorOpenSessions,
  payCollaboratorOpenSessions,
} from "src/apis/company";
import SkeletonLoad from "src/pages/components/SkeletonLoad";
import { formatCurrency } from "src/utils/currency";
import { formatDateToBR } from "src/utils/date";
import { extractErrorMessage } from "src/utils/http";

type CollaboratorPaymentDialogProps = {
  open: boolean;
  collaborator: Collaborator | null;
  onClose: () => void;
  onPaid: (result: CollaboratorPaymentResult) => void;
};

const CollaboratorPaymentDialog: FC<CollaboratorPaymentDialogProps> = ({
  open,
  collaborator,
  onClose,
  onPaid,
}) => {
  const [summary, setSummary] = useState<CollaboratorOpenSessionsSummary | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [paying, setPaying] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [result, setResult] = useState<CollaboratorPaymentResult | null>(null);

  const companyId = collaborator?.id_empresa;
  const collaboratorId = collaborator?.id_colaborador;

  useEffect(() => {
    if (!open || !companyId || !collaboratorId) {
      return;
    }

    setLoading(true);
    setError('');
    setResult(null);

    getCollaboratorOpenSessions(companyId, collaboratorId)
      .then(setSummary)
      .catch((err: unknown) => {
        setSummary(null);
        setError(extractErrorMessage(err, 'Erro ao carregar as sessões em aberto do colaborador.'));
      })
      .finally(() => setLoading(false));
  }, [open, companyId, collaboratorId]);

  const handleClose = useCallback(() => {
    if (paying) return;

    setSummary(null);
    setResult(null);
    setError('');
    onClose();
  }, [paying, onClose]);

  const handlePay = useCallback(async () => {
    if (!companyId || !collaboratorId || !summary) return;

    setPaying(true);
    setError('');

    try {
      const paymentResult = await payCollaboratorOpenSessions(
        companyId,
        collaboratorId,
        summary.valor_total_em_aberto,
      );

      setResult(paymentResult);
      onPaid(paymentResult);
    } catch (err: unknown) {
      setError(extractErrorMessage(err, 'Erro ao registrar o pagamento das sessões em aberto.'));
    } finally {
      setPaying(false);
    }
  }, [companyId, collaboratorId, summary, onPaid]);

  const hasOpenSessions = (summary?.qtde_sessoes_em_aberto ?? 0) > 0;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
          Sessões em aberto — {collaborator?.nome}
          {summary?.bloqueio && !result?.desbloqueado && (
            <Chip label="Bloqueado" color="error" size="small" />
          )}
          {result?.desbloqueado && (
            <Chip label="Desbloqueado" color="success" size="small" />
          )}
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <SkeletonLoad />
        ) : (
          <Box display="flex" flexDirection="column" gap={2}>
            {Boolean(error) && <Alert severity="error">{error}</Alert>}

            {result && (
              <Alert severity="success">
                {result.qtde_sessoes_quitadas} sessão(ões) quitada(s), totalizando{' '}
                {formatCurrency(result.valor_total_pago)}.
                {result.desbloqueado
                  ? ' O colaborador foi desbloqueado automaticamente.'
                  : ''}
              </Alert>
            )}

            {!result && !hasOpenSessions && !error && (
              <DialogContentText>
                Este colaborador não possui sessões em aberto.
              </DialogContentText>
            )}

            {!result && hasOpenSessions && (
              <>
                <DialogContentText>
                  O pagamento do valor total quita todos os atendimentos abaixo. Se o
                  colaborador estiver bloqueado, ele é desbloqueado automaticamente.
                </DialogContentText>

                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Atendimento</strong></TableCell>
                      <TableCell><strong>Data</strong></TableCell>
                      <TableCell align="right"><strong>Valor</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {summary?.sessoes_em_aberto?.map((session) => (
                      <TableRow key={session.id_atendimento}>
                        <TableCell>{session.id_atendimento}</TableCell>
                        <TableCell>{formatDateToBR(session.data)}</TableCell>
                        <TableCell align="right">{formatCurrency(session.valor)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                <Divider />

                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="subtitle1">
                    Total em aberto ({summary?.qtde_sessoes_em_aberto})
                  </Typography>
                  <Typography variant="h6">
                    {formatCurrency(summary?.valor_total_em_aberto)}
                  </Typography>
                </Box>
              </>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={paying}>
          {result ? 'Fechar' : 'Cancelar'}
        </Button>
        {!result && hasOpenSessions && (
          <Button variant="contained" onClick={handlePay} disabled={paying || loading}>
            {paying
              ? 'Registrando…'
              : `Registrar pagamento de ${formatCurrency(summary?.valor_total_em_aberto)}`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CollaboratorPaymentDialog;
